import { makeEvent } from "~/lib/signals";
import { store } from "~/state/store";
import { watch, watchCurrentUser } from "~/state/util";
import { CONFIG } from "~/config";
import { registerUser } from "~/login";
import { _ } from "~/i18n";
import Scene from "~/scene";
import { node } from "~/util/dom";
import { Unsubscribe } from "redux";
import { confirm } from "./ui/dialogs/confirm";

interface IDIMState {
	dim?: string;
	backTitle?: string;
	backAction?: () => void;
	onClose: () => void;
	search: any;
}

/**
 * Trida pro nacitani detailu mapyboxu a komunikaci s historii
 */
export default class DIMCurator {
	_dim: string;

	_lock: boolean;

	_likeData: any;

	_showLabel: number;

	_error: HTMLDivElement;

	_state: IDIMState;

	_storeUnsubscribe: Unsubscribe;

	/**
	 * konstruktor
	 */
	constructor() {
		this._dim = undefined;
		this._lock = false;
		this._likeData = null;
		this._showLabel = 0;
		this._error = node("div", { className: "dim-error" });
		this._error.addEventListener("click", this, false);
		this._state = null;

		const watcher = watch(store.getState, "user", watchCurrentUser);

		this._storeUnsubscribe = store.subscribe(watcher(() => {
			this._reload();
		}));
	}

	destroy() {
		this._storeUnsubscribe();
	}

	/**
	 * Nastaveni stavu (dim)
	 */
	setState(state?: IDIMState, data?: {
		ol?: string | number;
	}) {
		if (this._lock) {
			return this;
		}
		this._dim = state ? state.dim : undefined;
		this._showLabel = data && data.ol && CONFIG.inFrame && !Scene.isM3dActive() ? parseInt(data.ol.toString()) : 0;
		this._state = state;

		return this;
	}

	/**
	 * vracení stavu, zda mame ulozene nejake dim
	 * @return {Object} stav tridy, vraci dim, pokud nejake ma
	 */
	getState(): { dim?: string; } {
		if (!this._dim) {
			return {};
		}

		return {
			dim: this._dim,
		};
	}

	getActualItemState() {
		return this._state;
	}

	/**
	 * vrati data pro prvek ulozeny v moje mapy
	 * @return {Object} data
	 */
	getLikeData(): any {
		if (!this._dim) {
			return {};
		}

		return this._likeData;
	}

	/**
	 * nacteni dat
	 * @return {Promise} promise
	 */
	loadData(promptRewrite = false, beforeLoad = () => {}): Promise<void> {
		let promise = Promise.resolve();

		if (this._dim) {
			promise = Mapy.ProviderMapyBox.likeDetail(this._dim).then(async response => {
				let prms = Promise.resolve();

				if (response.status === 200 && response.like) {
					const data = response.like;
					const openCoords = pointData => {
						const coords = Scene.coordsFromWGS84(pointData.mark.lon, pointData.mark.lat);
						const state = {
							source: "coor",
							id: `${pointData.mark.lon},${pointData.mark.lat}`,
							show: true,
							markerIcon: "star-line",
							likeData: pointData,
							coords,
							gpsCoords: coords,
							showLabel: this._showLabel,
							...this._state?.backTitle ? { backTitle: this._state?.backTitle } : {},
							...this._state?.backAction ? { backAction: this._state?.backAction } : {},
							...this._state?.onClose ? { onClose: this._state?.onClose } : {},
							...this._state?.search ? { search: this._state.search } : {},
						};

						return Mapy.getComponent("detail").setState(state);
					};

					this._lock = true;

					// rucni mereni RO -> path
					if (data.type === "measure" && data.data.readonly) {
						data.type = "measureRO";
					}

					// nehrozi zruseni otevirani, takze rovnou provedeme beforeLoad
					if (data.type !== "route") {
						beforeLoad();
					}

					switch (data.type) {
						case "link":
							if (data.linktype === "track") {
								const state = {
									likeData: data,
									cat: "aktivity",
									...this._state?.backTitle ? { backTitle: this._state?.backTitle } : {},
									...this._state?.backAction ? { backAction: this._state?.backAction } : {},
									...this._state?.onClose ? { onClose: this._state?.onClose } : {},
								};

								Mapy.getComponent("activities").clear();

								Mapy.getComponent("mymaps").setState(state);
								if (!Mapy.getComponent("mymaps").isActive()) {
									Mapy.getComponent("mymaps").activate();
								}
								Mapy.getComponent("mymaps").activeComponent(Mapy.getComponent("activities"), true);
								Mapy.getComponent("activities").state = {
									showLabel: this._showLabel,
									...state,
								};
								this._likeData = data;
								this._lock = false;
							}
							break;

						case "entity":
							if (data.source === "coor") { // stara verze appky posila souradnici jako entity :(
								prms = openCoords(data).then(() => {
									this._lock = false;
									this._likeData = data;
								}, () => {});
							} else {
								const state = {
									data,
									likeData: data,
									showLabel: this._showLabel,
									show: true,
									...this._state?.backTitle ? { backTitle: this._state?.backTitle } : {},
									...this._state?.backAction ? { backAction: this._state?.backAction } : {},
									...this._state?.onClose ? { onClose: this._state?.onClose } : {},
									...this._state?.search ? { search: this._state.search } : {},
								};

								prms = Mapy.getComponent("detail").setState(state).then(() => {
									this._lock = false;
									this._likeData = data;
								}, () => {
									this._lock = false;
									openCoords(data);
									this._likeData = data;
									//this._notification(_("detail.loadError"), true);
								});
							}
							break;

						case "home":
						case "work":
							prms = new Promise(resolve => {
								Mapy.getComponent("detail").setState({
									source: data.source,
									id: data.source_id,
									...this._state?.backTitle ? { backTitle: this._state?.backTitle } : {},
									...this._state?.backAction ? { backAction: this._state?.backAction } : {},
									...this._state?.onClose ? { onClose: this._state?.onClose } : {},
								}).then(() => {
									this._lock = false;
									resolve();
								}, () => {
									this._lock = false;
									openCoords(data).then(() => {
										resolve();
									}, () => {});
								});
							});
							break;

						case "point":
							prms = openCoords(data).then(() => {
								this._lock = false;
								this._likeData = data;
							}, () => {});
							break;

						case "route":
							// pokud uz je routovac aktivni, tak se zeptame, jestli chceme jeho stav nahradit novym
							if (promptRewrite && !await confirm(_("alwaysOnMap.route.promptRewrite.cancel"), _("alwaysOnMap.route.promptRewrite.confirm"), _("alwaysOnMap.route.promptRewrite.body"))) {
								return Promise.reject();
							}

							beforeLoad();
							await Mapy.getComponent("route").activate();
							Mapy.getComponent("route").forceZoom();
							prms = Mapy.getComponent("route").routeFromRawData({
								likeData: data,
								showLabel: this._showLabel,
							}, data.data.routeParams).then(() => {
								this._lock = false;
								this._likeData = data;
							}, () => {});
							break;

						case "measure":
							prms = Mapy.getComponent("distanceMeter").activate().setState({
								likeData: data,
								showLabel: this._showLabel,
								...this._state?.backTitle ? { backTitle: this._state?.backTitle } : {},
								...this._state?.backAction ? { backAction: this._state?.backAction } : {},
								...this._state?.onClose ? { onClose: this._state?.onClose } : {},
							}).then(() => {
								this._lock = false;
								this._likeData = data;
							}, () => {});
							break;

						case "set":
							prms = Mapy.getComponent("userMarks").activate().setState({
								likeData: data,
								showLabel: this._showLabel,
								...this._state?.backTitle ? { backTitle: this._state?.backTitle } : {},
								...this._state?.backAction ? { backAction: this._state?.backAction } : {},
								...this._state?.onClose ? { onClose: this._state?.onClose } : {},
							}).then(() => {
								this._lock = false;
								this._likeData = data;
							}, () => {});
							break;

						case "measureRO":
						case "path":
							prms = Mapy.getComponent("path").activate().setState({
								likeData: data,
								showLabel: this._showLabel,
								...this._state?.backTitle ? { backTitle: this._state?.backTitle } : {},
								...this._state?.backAction ? { backAction: this._state?.backAction } : {},
								...this._state?.onClose ? { onClose: this._state?.onClose } : {},
							}).then(() => {
								this._lock = false;
								this._likeData = data;
							}, error => {
								// eslint-disable-next-line no-console
								console.error(error);
							});
							break;
						default:
							break;
					}

					Mapy.getComponent("logger").log("mymaps-item-show", { type: data.type, origin: this._dim });

					return prms;
				}

				return null;
			}, () => {
				if (Mapy.getComponent("history").isFromHistory()) {
					Mapy.getComponent("route").clear();
					while (Mapy.active.length) {
						Mapy.active[0].deactivate();
					}

					this._error.innerHTML = _("dimcurator.errorDetail");
					const { user } = store.getState().user;

					if (user.state === "green") {
						this._error.insertAdjacentHTML("beforeend", _("dimcurator.errorDetailLink"));
					} else {
						this._error.insertAdjacentHTML("beforeend", _("dimcurator.errorDetailNotLogged"));
					}

					const parent = Mapy.getComponent("layout").getBody();

					parent.appendChild(this._error);
				} else {
					makeEvent("dim-is-not-share", this, { dim: this._dim });
				}

				return Promise.resolve();
			});
		}

		// TODO
		// potrba dodelat, aby kdyz nedojde detail, protoze enni muj a neni sdileny, nebo uz neexistuje, tak nejaka obrazovka, ktera to uzivateli rekne

		return promise;
	}

	/**
	 * odebrani errorove hlasky o nenacteni detailu z Moje mapy
	 */
	removeError() {
		if (this._error.parentNode) {
			this._error.remove();
			this.setState();
		}
	}

	handleEvent(event: MouseEvent) {
		if (event.type === "click") {
			event.preventDefault();

			const target = event.target as HTMLElement;

			if (target.classList.contains("error-register")) {
				registerUser();
			} else if (target.classList.contains("error-login")) {
				Mapy.getComponent("login").open({
					motivation: "none",
				});
			} else if (target.classList.contains("error-home-mymaps")) {
				this.removeError();
				Mapy.getComponent("mymaps").activate();
				Mapy.getComponent("mymaps").setState({
					cat: "mista-trasy",
				});
				Mapy.getComponent("history").save();
			}
		}
	}

	/**
	 * vyvolani prenacteni dat
	 */
	_reload() {
		if (this._dim) {
			this.loadData();
		}
	}
}
